/* eslint-disable @typescript-eslint/no-explicit-any */
import useAlertService from "@mirage/hooks/useAlertService";
import { DateFieldPopper, EditablePredefinedViewFields, EntityTableContext, EntityTableProvider, UpdatePredefinedView } from "@mirage/store/entityTableStore";
import { TablePaginationBaseProps } from "@mui/material/TablePagination";
import {
    DataGridPremium,
    DataGridPremiumProps,
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    GridColDef,
    GridColumnHeaderParams,
    GridColumnResizeParams,
    GridColumnVisibilityModel,
    GridEventListener,
    GridFilterModel,
    GridPinnedColumns,
    GridRenderCellParams,
    GridRowClassNameParams,
    GridRowId,
    GridRowModesModel,
    GridRowParams,
    GridSortItem,
    GridSortModel,
    GridToolbarColumnsButton,
    LicenseInfo,
    MuiEvent,
    useGridApiRef,
} from "@mui/x-data-grid-premium";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { Filter } from "@shared/mirage/entityTable/Filter";
import { EntityTableHelper } from "@shared/mirage/helpers/EntityTableHelper";
import { EntityBinding, Relations } from "@shared/mirage/metadata/BaseEntityRecordBinding";
import _, { cloneDeep } from "lodash";
import { Dispatch, ReactNode, SetStateAction, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomNoRowsOverlay from "./CustomNoRowsOverlay";
import { EntityHelper } from "./EntityTableHelper";
import { EntityTableStateStorage } from "./EntityTableStateStorage";
import ColumnMenu from "./components/ColumnMenu";
import CustomGridToolbar, { CustomGridToolbarOptions } from "./components/CustomGridToolbar";
import DataGridActionsColumn, { DataGridActionsColumnProps } from "./components/DataGridActionsColumn";
import Footer, { GridLegendItem } from "./components/Footer";
import DateFilterModal from "./components/GridDatesFilter/DateFilterModal";
import { MetadataRegistry } from "@shared/mirage/metadata/MetaBinding";
import { UserPreferencesService } from "@mirage/services/UserPreferencesService";
import { IUserTablePreferences, PredefinedEntityTableView, PredefinedEntityTableViewId, UserPreferenceTypes } from "@shared/mirage/services/IUserPreferencesService";
import { useRecoilState, useRecoilValue } from "recoil";
import { userPreferencesState, userState } from "@mirage/store/UserStore";
import { Collapse, MenuItem, Typography, useTheme } from "@mui/material";
import { AdvancedQueryBuilderModal } from "./components/advancedQueryBuilder/AdvancedQueryBuilderModal";
import { Field, RuleGroupTypeIC, RuleType } from "react-querybuilder";
import { useConfirmDialog } from "@mirage/hooks/useConfirmDialog";
import { useBoolean } from "@mirage/hooks/useBoolean";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { OrganizePredefinedViewsModal } from "./components/OrganizePredefinedViewsModal";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { StringsHelper } from "@shared/mirage/helpers/StringsHelper";
import { currentApplication } from "@mirage/store/currentApplicationStore";
import { GlobalPermissions } from "@shared/mirage/core/contracts/IPermission";
import { DOCUMENTS_TABLE_ACTION_COLUMN_FIELD } from "@mirage/components/documentsTable/DocumentsTable";

const userPreferencesService = new UserPreferencesService();

LicenseInfo.setLicenseKey("58344cf7ee962fcd863deef4a2bafcf7Tz0xMDQwMjUsRT0xNzY1NDQ0MTA2MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y");

export interface EntityTableProps
    extends Omit<
        DataGridPremiumProps,
        | "rows"
        | "rowCount"
        | "loading"
        | "paginationMode"
        | "onPaginationModelChange"
        | "paginationModel"
        | "filterMode"
        | "onFilterModelChange"
        | "sortingMode"
        | "onSortModelChange"
        | "initialState"
        | "autoHeight"
        | "hideFooter"
        | "onPinnedColumnsChange"
        | "filterModel"
    > {
    onPageRequest: (page: IEntityPageRequest) => Promise<any>;
    key: string;
    stateKey: string;
    entityName?: string; // should be required - to do
    title?: string;
    /**
     * @default "true"
     */
    inlineTitle?: boolean;
    noRecordsImage?: boolean;
    noRecordsMessage?: string;
    filter?: Filter;
    onRecordSelected?: (record: any) => void;
    /**
     * @deprecated use entityName instead
     */
    relations?: Relations;
    forceFetch?: boolean;
    toolbarOptions?: CustomGridToolbarOptions;
    actionsColumn?: Omit<DataGridActionsColumnProps, "setTableState" | "gridRef">;
    customTogglePanelCol?: ICustomTogglePanelCol;
    onEditRecordSave?: (id: GridRowId) => Promise<void>;
    excludeBaseColumns?: boolean;
    disableFilterOnBaseColumns?: boolean;
    showAll?: boolean;
    /**
     * @deprecated use entityName instead
     */
    metadata?: EntityBinding<unknown>;
    hideEmpty?: boolean;
    hideRowsPerPage?: boolean;
    hideToolbar?: boolean;
    showQuickSearch?: boolean;
    quickSearchAutofocus?: boolean;
    quickSearchColumns?: string[];
    legend?: GridLegendItem[];
    gridHeight?: string;
    gridMaxHeight?: string;
    pageSize?: number;
    /**
     * @default "true"
     */
    pagination?: boolean;
    /**
     * Columns visibility model affects data being fetched from the server, this flag can override this behavior
     * @default "false"
     */
    fetchAllColumns?: boolean;
    overridePaginationProps?: (baseProps: TablePaginationBaseProps) => TablePaginationBaseProps;
    experimentalQueryBuilder?: boolean;
    filteredEntityTableOnChange?: (setter: Dispatch<SetStateAction<ITableState>>) => void;
    overrideColumns?: (entityName: string | undefined, columns: GridColDef[]) => void;
    constantPageSize?: number;
}

export interface ITableState {
    rows: any[];
    total: number;
    pageIndex: number;
    pageSize: number;
    sortModel: GridSortItem[];
    customFilter: Filter | null | undefined;
    filterModel: GridFilterModel;
    isLoading: boolean;
    rowModesModel: GridRowModesModel;
    selectedRow: { id: GridRowId } | null;
    columnVisibilityModel?: GridColumnVisibilityModel;
    pinnedColumns?: GridPinnedColumns;
    predefinedEntityTableViews: PredefinedEntityTableView[];
    predefinedEntityTableViewsOrder: string[];
    selectedPredefinedEntityTableViewId: PredefinedEntityTableViewId;
    forceFetch: boolean;
    queryBuilderIsOpen: boolean;
    viewOrganizeModalIsOpen: boolean;
    queryBuilderFields: Field[];
    uiSettingsLoading: boolean;
}

export type ModifyView = { name: string; query: RuleGroupTypeIC<RuleType<string, string, any, string>, string> | null };
const EMPTY_MODIFY_VIEW: ModifyView = { name: "", query: null };
const DefaultPageSizeOptions = [10, 15, 20, 30];
const EntityTable = memo((props: EntityTableProps) => {
    const {
        stateKey,
        actionsColumn,
        hideEmpty,
        legend,
        entityName,
        fetchAllColumns = false,
        experimentalQueryBuilder,
        filteredEntityTableOnChange,
        overrideColumns,
        pageSizeOptions,
        constantPageSize,
    } = props;
    const [showDialog] = useConfirmDialog();
    const showAlert = useAlertService();
    const currentTheme = useTheme();
    const currentAppName = useRecoilValue(currentApplication);
    const isPublicOnlyMode = !!currentTheme.entityTable?.publicOnly;
    const excludeBaseColumns = props.excludeBaseColumns || !!isPublicOnlyMode;
    const [userPreferences, setUserPreferences] = useRecoilState(userPreferencesState);
    const userPreferencesKey = useMemo(() => EntityHelper.getUserPreferencesKey(stateKey), [stateKey]);
    const userSettingsExists = Boolean(userPreferences?.entityTables?.[userPreferencesKey]);
    const columnsResized = useRef(false);
    const toolbarSetAnchor = useRef<((el: HTMLElement | null) => void) | null>(null);
    const copyOfPredefinedViewsList = useRef<PredefinedEntityTableView[]>([]);
    const { value: isPageSizeMenuOpen, toggle: toggleOpenPageSizeMenu } = useBoolean(false);
    const initialRequest = useMemo(() => EntityTableStateStorage.getState(stateKey), [stateKey]);
    const formattedSortModel = useMemo(() => EntityHelper.createSortModel(initialRequest?.order, props.sortModel), [initialRequest?.order, props.sortModel]);
    const columnVisibilityModel = useMemo(
        () => EntityHelper.prepareColumnVisibilityModel(props.columns, props.columnVisibilityModel, excludeBaseColumns, formattedSortModel),
        [excludeBaseColumns, formattedSortModel, props.columnVisibilityModel, props.columns]
    );

    const [tableState, setTableState] = useState({
        rows: [],
        total: 0,
        pageIndex: EntityHelper.getPageIndex(initialRequest),
        pageSize: constantPageSize || props.pageSize || 10,
        isLoading: true,
        sortModel: formattedSortModel,
        customFilter: initialRequest?.filterModel?.items.length ? EntityHelper.prepareFilterObject(initialRequest?.filterModel) : null,
        filterModel: initialRequest?.filterModel || ({ items: [] } as GridFilterModel),
        rowModesModel: {} as GridRowModesModel,
        selectedRow: null,
        columnVisibilityModel: columnVisibilityModel,
        pinnedColumns: props.pinnedColumns || ({} as GridPinnedColumns),
        predefinedEntityTableViews: [] as PredefinedEntityTableView[],
        predefinedEntityTableViewsOrder: [] as string[],
        selectedPredefinedEntityTableViewId: "",
        queryBuilderIsOpen: false,
        viewOrganizeModalIsOpen: false,
        queryBuilderFields: [],
        forceFetch: false,
        uiSettingsLoading: true,
    } as ITableState);
    const selectedView = useMemo(
        () => tableState.predefinedEntityTableViews.find((view) => view.id === tableState.selectedPredefinedEntityTableViewId),
        [tableState.predefinedEntityTableViews, tableState.selectedPredefinedEntityTableViewId]
    );
    const currentEntityName = useMemo(() => (selectedView?.entityName ? selectedView?.entityName : entityName), [entityName, selectedView?.entityName]);
    const metadata = currentEntityName ? MetadataRegistry.get(currentEntityName) : entityName ? MetadataRegistry.get(entityName) : props.metadata;
    const relations = metadata?.relations || props.relations || {};
    const user = useRecoilValue(userState);
    const userPermissions = user.permissions || [];
    const hasSettingsPermission = userPermissions.findIndex((p) => p.name == GlobalPermissions.Settings) > -1;
    const selectedViewIsAdminDefined = useMemo(() => selectedView?.isAdministratorView, [selectedView?.isAdministratorView]);
    const selectedViewIsStatmapDefined = useMemo(() => selectedView?.isStatmapView, [selectedView?.isStatmapView]);
    const [modifyView, setModifyView] = useState<ModifyView>(EMPTY_MODIFY_VIEW);
    const isOutOfScope = tableState.pageIndex > 0 && !tableState.rows.length;
    const internalForceFetch = useCallback(() => setTableState((prev) => ({ ...prev, forceFetch: !prev.forceFetch })), []);
    const [dateFilterPopper, setDateFilterPopper] = useState<DateFieldPopper>({ open: false, elementRef: null, fieldName: "" });
    const updatePredefinedEntityTableViewsOrder = useCallback((viewId: PredefinedEntityTableViewId, destinationIndx: number) => {
        setTableState((prev) => {
            const viewsOrder = [...prev.predefinedEntityTableViewsOrder];
            const viewIdx = viewsOrder.findIndex((v) => v === viewId);
            if (viewIdx < 0) {
                viewsOrder.splice(destinationIndx, 0, viewId);
                return { ...prev, predefinedEntityTableViewsOrder: viewsOrder };
            }
            const [removed] = viewsOrder.splice(viewIdx, 1);
            if (!removed) {
                //place viewId in destination index
                viewsOrder.splice(destinationIndx, 0, viewId);
                return { ...prev, predefinedEntityTableViewsOrder: viewsOrder };
            }
            viewsOrder.splice(destinationIndx, 0, removed);
            if (uiSettings.current) {
                uiSettings.current.predefinedEntityTableViewsOrder = [...viewsOrder];
            }
            return { ...prev, predefinedEntityTableViewsOrder: viewsOrder };
        });
    }, []);
    const orderedPredefinedTableViews = useMemo(() => {
        const copyOfPredefinedViewsList = cloneDeep(tableState.predefinedEntityTableViews);
        return copyOfPredefinedViewsList.sort(
            (a, b) => tableState.predefinedEntityTableViewsOrder.indexOf(a.id.toString()) - tableState.predefinedEntityTableViewsOrder.indexOf(b.id.toString())
        );
    }, [tableState.predefinedEntityTableViews, tableState.predefinedEntityTableViewsOrder]);

    const openAdvancedQueryBuilder = useCallback(() => {
        const currentTableView = tableState.predefinedEntityTableViews.find((view) => view.id === tableState.selectedPredefinedEntityTableViewId);
        if (!currentTableView) {
            return;
        }
        setModifyView((p) => {
            return { ...p, name: currentTableView.name, query: currentTableView.clientQuery || { rules: [] } };
        });
        setTableState((prev) => ({ ...prev, queryBuilderIsOpen: true }));
    }, [tableState.predefinedEntityTableViews, tableState.selectedPredefinedEntityTableViewId]);
    const openOrganizeViewsDialog = useCallback(() => {
        copyOfPredefinedViewsList.current = cloneDeep(tableState.predefinedEntityTableViews);
        setTableState((prev) => ({ ...prev, viewOrganizeModalIsOpen: true }));
    }, [tableState.predefinedEntityTableViews]);
    const closeAdvancedQueryBuilder = useCallback(() => setTableState((prev) => ({ ...prev, queryBuilderIsOpen: false })), []);
    const closeViewOrganizeModal = useCallback(() => setTableState((prev) => ({ ...prev, viewOrganizeModalIsOpen: false })), []);
    const onViewSave = useCallback(() => {
        const errorMsg = EntityHelper.validateModifyViewName(modifyView.name);
        if (errorMsg) {
            showAlert(errorMsg, "error");
            return;
        }
        closeAdvancedQueryBuilder();
        setTableState((prev) => {
            return {
                ...prev,
                predefinedEntityTableViews: [
                    ...prev.predefinedEntityTableViews.map((view) =>
                        view.id === prev.selectedPredefinedEntityTableViewId ? { ...view, name: modifyView.name, clientQuery: modifyView?.query } : view
                    ),
                ],
            };
        });
        setModifyView(EMPTY_MODIFY_VIEW);
        internalForceFetch();
    }, [closeAdvancedQueryBuilder, internalForceFetch, modifyView.name, modifyView?.query, showAlert]);
    const onOrganizeViewsSave = useCallback(() => {
        const atLeastOneViewIsNotValid = tableState.predefinedEntityTableViews.find(
            (view) => {
                if (view.isStatmapView) {
                    return false;
                }
                const errorMsg = EntityHelper.validateModifyViewName(view.name);
                if (errorMsg) {
                    showAlert(`Some of the views are invalid. ${errorMsg}`, "error");
                    return true;
                }
                return false;
            },
            [tableState.predefinedEntityTableViews]
        );
        if (atLeastOneViewIsNotValid) {
            return;
        }
        closeViewOrganizeModal();
    }, [closeViewOrganizeModal, showAlert, tableState.predefinedEntityTableViews]);
    const onOrganizeViewsCancel = useCallback(() => {
        setTableState((prev) => {
            return { ...prev, predefinedEntityTableViews: copyOfPredefinedViewsList.current };
        });
        closeViewOrganizeModal();
    }, [closeViewOrganizeModal]);
    const onViewCancel = useCallback(() => {
        closeAdvancedQueryBuilder();
        setModifyView(EMPTY_MODIFY_VIEW);
        internalForceFetch();
    }, [closeAdvancedQueryBuilder, internalForceFetch]);
    const updateAdvancedQueryBuilderName = useCallback((name: string) => setModifyView((p) => ({ ...p, name })), []);
    const updatePredefinedView: UpdatePredefinedView = useCallback(
        <Key extends keyof EditablePredefinedViewFields>(field: Key, value: PredefinedEntityTableView[Key], viewId: string) =>
            setTableState((prev) => {
                return {
                    ...prev,
                    predefinedEntityTableViews: prev.predefinedEntityTableViews.map((view) => (view.id === viewId ? { ...view, [field]: value } : view)),
                };
            }),
        []
    );
    const updateAdvancedQueryBuilderQuery = useCallback(
        (query: RuleGroupTypeIC<RuleType<string, string, any, string>, string>) => {
            setModifyView((p) => {
                return { ...p, query };
            });
            internalForceFetch();
        },
        [internalForceFetch]
    );
    const uiSettings = useRef<IUserTablePreferences | null>(null);
    const apiRef = useGridApiRef();
    const gridApiRef = props.apiRef || apiRef;
    const [panelAnchorEl, setPanelAnchorEl] = useState<HTMLElement | null>(null);
    EntityHelper.setLookupTypeForRelationColumns(props.columns, relations);
    EntityHelper.setRequiredColumns(props.columns, metadata?.columns);
    const setActionsColumn = useCallback(
        (columns: GridColDef<any, any, any>[]) => {
            if (!!actionsColumn?.actions || !!actionsColumn?.deleteAction || !!actionsColumn?.editAction) {
                columns.unshift(DataGridActionsColumn({ setTableState, gridRef: gridApiRef, ...actionsColumn }));
            }
        },
        [actionsColumn, gridApiRef]
    );

    const setPanelToggleColumn = useCallback(() => {
        if (props.customTogglePanelCol) {
            GRID_DETAIL_PANEL_TOGGLE_COL_DEF.headerName = props.customTogglePanelCol?.headerName;
            GRID_DETAIL_PANEL_TOGGLE_COL_DEF.renderCell = props.customTogglePanelCol?.renderCell;
        }
    }, [props.customTogglePanelCol]);
    const updateUiSettingsView = useCallback(
        <Key extends keyof PredefinedEntityTableView>(viewId: PredefinedEntityTableViewId, key: Key, value: PredefinedEntityTableView[Key]) => {
            if (!tableState?.predefinedEntityTableViews?.length) {
                uiSettings.current = { ...uiSettings.current, [key]: value };
                return;
            }
            const view = tableState.predefinedEntityTableViews.find((v) => v.id === viewId);
            if (!view || !uiSettings.current || !uiSettings.current.predefinedEntityTableViews) {
                return;
            }
            const updatedViews = uiSettings.current.predefinedEntityTableViews.map((view) => {
                if (view.id === uiSettings.current?.selectedPredefinedEntityTableViewId) {
                    return { ...view, [key]: value };
                }
                return view;
            });
            uiSettings.current = { ...uiSettings.current, predefinedEntityTableViews: updatedViews };
        },
        [tableState.predefinedEntityTableViews]
    );
    const applyFilterOnHeader = useCallback(
        (columns: GridColDef[]) => {
            if (!gridApiRef.current) {
                return;
            }
            if (isPublicOnlyMode) {
                return columns;
            }
            columns.forEach((column) => {
                column.renderHeader = (params: GridColumnHeaderParams) => {
                    const filter = tableState.filterModel.items.find((item) => item.field === column.field);
                    const originalValue: string | string[] | undefined = filter?.value;
                    let filterValue = Array.isArray(originalValue) ? originalValue.join(", ") : originalValue;
                    if (!filterValue && filter?.operator) {
                        filterValue = filter.operator === "isEmpty" ? "(is empty)" : filter.operator === "isNotEmpty" ? "(is not empty)" : "";
                    }
                    if (!params?.colDef?.headerName) {
                        return null;
                    }
                    const element = (
                        <span>
                            <strong
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const elementRef = gridApiRef.current.getColumnHeaderElement(column.field);
                                    setPanelAnchorEl(elementRef);
                                    if (column.type === "date" || column.type === "datetime") {
                                        gridApiRef.current.hideFilterPanel();
                                        setDateFilterPopper({ open: true, elementRef, fieldName: column.field });
                                    } else {
                                        gridApiRef.current.showFilterPanel(params.field);
                                    }
                                }}
                            >
                                {params.colDef.headerName}
                            </strong>
                            <div
                                style={{
                                    fontSize: "0.75rem",
                                    lineHeight: "0.75rem",
                                    position: "absolute",
                                    bottom: "4px",
                                    fontWeight: 500,
                                    color: "rgb(66, 165, 245)",
                                    // textTransform: "uppercase",
                                }}
                            >
                                {filterValue}
                            </div>
                        </span>
                    );

                    return element;
                };
            });
        },
        [gridApiRef, isPublicOnlyMode, tableState.filterModel.items]
    );

    const columns = EntityHelper.formatColumns(
        cloneDeep(props.columns),
        excludeBaseColumns,
        setActionsColumn,
        setPanelToggleColumn,
        props.disableFilterOnBaseColumns,
        metadata?.columns,
        formattedSortModel,
        applyFilterOnHeader,
        uiSettings.current
    );
    const selectPredefinedEntityTable = useCallback(
        (id: PredefinedEntityTableViewId) => {
            const view = tableState.predefinedEntityTableViews.find((v) => v.id === id);
            if (!view) {
                return;
            }
            uiSettings.current = {
                ...uiSettings.current,
                selectedPredefinedEntityTableViewId: id,
            };
            setTableState((prev) => {
                const newColumnVisibilityModel = view.columnVisibilityModel || columnVisibilityModel;
                const newPinnedColumns = view.pinnedColumns || props.pinnedColumns || {};
                const newPageSize = constantPageSize || view.pageSize || props.pageSize || 10;
                const newCustomFilter = view.customFilter;
                const newFilterModel = view.filterModel;
                return {
                    ...prev,
                    selectedPredefinedEntityTableViewId: id,
                    columnVisibilityModel: newColumnVisibilityModel,
                    pinnedColumns: newPinnedColumns,
                    pageSize: newPageSize,
                    customFilter: newCustomFilter,
                    filterModel: newFilterModel,
                    rows: [],
                    predefinedEntityTableViews: prev.predefinedEntityTableViews.map((v) => {
                        if (v.id !== prev.selectedPredefinedEntityTableViewId) {
                            return v;
                        }
                        return {
                            ...v,
                            columns: columns,
                            filterModel: prev.filterModel,
                            customFilter: prev.customFilter,
                            columnVisibilityModel: prev.columnVisibilityModel,
                            pinnedColumns: prev.pinnedColumns,
                            pageSize: prev.pageSize,
                        };
                    }),
                };
            });
            internalForceFetch();
        },
        [columnVisibilityModel, columns, internalForceFetch, props.pageSize, props.pinnedColumns, tableState.predefinedEntityTableViews, constantPageSize]
    );
    const deleteTableView = useCallback(async () => {
        const selectedTableViewName = tableState.predefinedEntityTableViews.find((view) => view.id === tableState.selectedPredefinedEntityTableViewId)?.name;
        if (!selectedTableViewName) {
            return;
        }
        const deleteConfirmed = await showDialog({
            header: `Delete table view - ${selectedTableViewName}`,
            mutation: "Are you sure you want to delete this table view?",
            onConfirmColor: "error",
            onConfirmLabel: "Delete",
            onRejectLabel: "Cancel",
        });
        if (!deleteConfirmed) {
            return;
        }
        let isAdminView = false;
        let newSelectedViewId = "";
        setModifyView(EMPTY_MODIFY_VIEW);
        setTableState((prev) => {
            const indexOfSelected = prev.predefinedEntityTableViews.findIndex((view) => view.id === prev.selectedPredefinedEntityTableViewId);
            isAdminView = prev.predefinedEntityTableViews[indexOfSelected].isAdministratorView;
            const viewBeforeSelected = prev.predefinedEntityTableViews[indexOfSelected - 1] || { id: -1, pageSize: 10 };
            newSelectedViewId = viewBeforeSelected.id;
            return {
                ...prev,
                pageSize: viewBeforeSelected.pageSize || 10,
                pageIndex: 0,
                total: 0,
                filterModel: viewBeforeSelected.filterModel,
                customFilter: viewBeforeSelected.customFilter,

                predefinedEntityTableViews: prev.predefinedEntityTableViews.filter((view) => view.id !== prev.selectedPredefinedEntityTableViewId),
                selectedPredefinedEntityTableViewId: newSelectedViewId,
            };
        });

        // Update UI settings
        if (uiSettings.current?.predefinedEntityTableViews) {
            const indexOfSelected = uiSettings.current.predefinedEntityTableViews.findIndex((view) => view.id === tableState.selectedPredefinedEntityTableViewId);
            const viewBeforeSelected = uiSettings.current.predefinedEntityTableViews[indexOfSelected - 1] || { id: -1, pageSize: 10 };
            uiSettings.current.predefinedEntityTableViews =
                uiSettings.current.predefinedEntityTableViews?.filter((view) => {
                    if (view.id !== tableState.selectedPredefinedEntityTableViewId) {
                        return view;
                    }
                }) || [];
            uiSettings.current.selectedPredefinedEntityTableViewId = viewBeforeSelected.id;
        }

        // Select the new view to ensure its settings are applied
        if (newSelectedViewId) {
            selectPredefinedEntityTable(newSelectedViewId);
        }

        closeAdvancedQueryBuilder();

        userPreferencesService.deletePreference({
            preference: UserPreferenceTypes.ENTITY_TABLES,
            key: userPreferencesKey,
            isAdminView,
            appName: currentAppName,
            predefinedEntityTableViewId: tableState.selectedPredefinedEntityTableViewId,
        });
        internalForceFetch();
    }, [
        closeAdvancedQueryBuilder,
        selectPredefinedEntityTable,
        currentAppName,
        internalForceFetch,
        showDialog,
        tableState.predefinedEntityTableViews,
        tableState.selectedPredefinedEntityTableViewId,
        userPreferencesKey,
    ]);

    const copyAsPredefinedEntityTableView = useCallback(() => {
        const id = StringsHelper.generateUUID();
        setTableState((prev) => {
            const selectedView = prev.predefinedEntityTableViews.find((v) => v.id === prev.selectedPredefinedEntityTableViewId);
            if (!selectedView && !metadata?.entityName) {
                console.error("No selected view or metadata found");
                return prev;
            }
            return {
                ...prev,
                predefinedEntityTableViews: [
                    ...prev.predefinedEntityTableViews,
                    {
                        id,
                        name: "New view",
                        description: "",
                        entityName: selectedView?.entityName || metadata?.entityName || "",
                        colorChanged: false,
                        isStatmapView: false,
                        color: MirageTheme.palette.primary.main,
                        isAdministratorView: false,
                        columns: columns,
                        customFilter: EntityHelper.prepareFilterObject(prev.filterModel),
                        pageSize: selectedView?.pageSize ? selectedView.pageSize : prev.pageSize,
                        filterModel: selectedView?.filterModel ? selectedView.filterModel : prev.filterModel,
                        clientQuery: selectedView
                            ? selectedView.clientQuery
                            : {
                                  rules: [],
                              },
                        columnOrder: selectedView?.columnOrder ? selectedView.columnOrder : uiSettings.current?.columnOrder || props.columns.map((el) => el.field),
                        columnVisibilityModel: selectedView?.columnVisibilityModel ? selectedView.columnVisibilityModel : prev.columnVisibilityModel,
                        pinnedColumns: selectedView?.pinnedColumns ? selectedView.pinnedColumns : prev.pinnedColumns,
                    },
                ],
                selectedPredefinedEntityTableViewId: id,
                predefinedEntityTableViewsOrder: [...prev.predefinedEntityTableViewsOrder, id],
            };
        });
        if (id) {
            selectPredefinedEntityTable(id);
        }
    }, [columns, metadata?.entityName, props.columns, selectPredefinedEntityTable]);
    const saveUiPreferences = useCallback(() => {
        if (!userPreferencesKey) {
            return;
        }
        const ui = uiSettings.current;
        if (!ui) {
            return;
        }
        setUserPreferences((prev) => {
            return {
                ...prev,
                entityTables: {
                    ...prev.entityTables,
                    [userPreferencesKey]: ui,
                },
            };
        });
        userPreferencesService.savePreferences({ preference: UserPreferenceTypes.ENTITY_TABLES, key: userPreferencesKey, value: ui, appName: currentAppName });
        columnsResized.current = false;
    }, [currentAppName, setUserPreferences, userPreferencesKey]);

    const rowMode = useMemo(() => {
        if (!tableState.selectedRow) {
            return "view";
        }
        const { id } = tableState.selectedRow;
        return tableState.rowModesModel[id]?.mode || "view";
    }, [tableState.rowModesModel, tableState.selectedRow]);

    useEffect(() => {
        const copy = { ...uiSettings.current };
        uiSettings.current = {
            ...copy,
            pageSize: tableState.pageSize,
            columnVisibilityModel: tableState.columnVisibilityModel,
            pinnedColumns: tableState.pinnedColumns,
            predefinedEntityTableViews: tableState.predefinedEntityTableViews,
            predefinedEntityTableViewsOrder: tableState.predefinedEntityTableViewsOrder,
            selectedPredefinedEntityTableViewId: tableState.selectedPredefinedEntityTableViewId,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState.predefinedEntityTableViews, tableState.selectedPredefinedEntityTableViewId]);

    useEffect(() => {
        if (isOutOfScope && !tableState.isLoading) {
            setTableState((p) => {
                return { ...p, pageIndex: 0 };
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOutOfScope]);

    useEffect(() => {
        if (!userPreferencesKey) {
            return;
        }
        const fetchSettings = () => {
            const settings = userPreferences?.entityTables?.[userPreferencesKey];
            setTableState((prev) => ({ ...prev, uiSettingsLoading: true }));
            if (!settings) {
                console.info(`No user settings found`);
                setTableState((prev) => ({ ...prev, uiSettingsLoading: false }));
                return;
            }
            uiSettings.current = { ...settings };
            const ensureViewsOrder = (currentOrder: string[] = [], allViews?: PredefinedEntityTableView[]) => {
                if (!currentOrder.length && !allViews?.length) {
                    return currentOrder;
                }
                if (!currentOrder.length) {
                    return allViews?.map((v) => v.id.toString()) || [];
                }
                const currentOrderWithoutMissingViews = currentOrder.filter((v) => allViews?.find((view) => view.id.toString() === v.toString()));
                const viewsNotInOrder = allViews?.filter((v) => !currentOrderWithoutMissingViews.includes(v.id.toString()));
                if (viewsNotInOrder?.length) {
                    viewsNotInOrder.forEach((v) => {
                        if (v.isAdministratorView) {
                            currentOrderWithoutMissingViews.unshift(v.id.toString());
                        } else {
                            currentOrderWithoutMissingViews.push(v.id.toString());
                        }
                    });
                }
                return currentOrderWithoutMissingViews.filter((v) => allViews?.find((view) => view.id.toString() === v.toString()));
            };
            const ensureSelectedView = (savedSelectedViewId?: string, availableViews?: PredefinedEntityTableView[]) => {
                const savedSelectedViewIdIsAvailable = availableViews?.find((v) => v.id === savedSelectedViewId);
                if (savedSelectedViewIdIsAvailable) {
                    return savedSelectedViewId;
                }
                if (availableViews && availableViews[0]) {
                    return availableViews[0].id;
                }
                return "";
            };
            setTableState((prevState) => {
                const views = settings.predefinedEntityTableViews || prevState.predefinedEntityTableViews;
                const savedSelectedViewId = settings.selectedPredefinedEntityTableViewId || prevState.selectedPredefinedEntityTableViewId;
                const selectedPredefinedEntityTableViewId = ensureSelectedView(savedSelectedViewId, views);
                const selectedViewSettings = settings.predefinedEntityTableViews?.find((view) => view.id === selectedPredefinedEntityTableViewId);
                const pageSize = constantPageSize || selectedViewSettings?.pageSize || settings.pageSize || props.pageSize || prevState.pageSize;

                const newState = {
                    ...prevState,
                    pageSize: pageSize,
                    columnVisibilityModel: selectedViewSettings?.columnVisibilityModel || settings.columnVisibilityModel || prevState.columnVisibilityModel,
                    pinnedColumns: selectedViewSettings?.pinnedColumns || settings.pinnedColumns || prevState.pinnedColumns,
                    predefinedEntityTableViews: props.experimentalQueryBuilder ? views : [],
                    predefinedEntityTableViewsOrder: props.experimentalQueryBuilder ? ensureViewsOrder(settings.predefinedEntityTableViewsOrder, views) : [],
                    selectedPredefinedEntityTableViewId: props.experimentalQueryBuilder ? selectedPredefinedEntityTableViewId : null,
                    filterModel: selectedViewSettings?.filterModel || prevState.filterModel,
                    customFilter: selectedViewSettings?.customFilter || prevState.customFilter,
                    uiSettingsLoading: false,
                } as ITableState;

                return newState; // Update state
            });
        };
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSettingsExists, userPreferencesKey]);

    useEffect(() => {
        const prepareAdvancedQueryBuilderFields = () => {
            if (!metadata) {
                return;
            }
            const fields = EntityHelper.prepareAdvancedQueryBuilderFields(metadata);
            setTableState((prev) => {
                return { ...prev, queryBuilderFields: fields };
            });
        };

        prepareAdvancedQueryBuilderFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadata?.entityName]);

    useEffect(() => {
        if (typeof filteredEntityTableOnChange === "function") {
            filteredEntityTableOnChange(setTableState);
        }
    }, [filteredEntityTableOnChange]);

    useEffect(() => {
        const onRecordsFetch = async () => {
            if (tableState.uiSettingsLoading) {
                return;
            }
            const filter = EntityTableHelper.MergeFilters(props.filter, tableState.customFilter);
            const millisecondsLoadingStateTimeout = 1200;
            const loadingTimeout = setTimeout(() => setTableState((prevState) => ({ ...prevState, isLoading: true })), millisecondsLoadingStateTimeout);

            try {
                const selectedView = tableState.predefinedEntityTableViews.find((view) => view.id === tableState.selectedPredefinedEntityTableViewId);
                const prColumns = selectedView && selectedView.columns ? selectedView.columns : columns;
                let pageRequest = EntityHelper.preparePageRequest(tableState, filter, prColumns, fetchAllColumns, modifyView);
                const existingState = { ...EntityTableStateStorage.getState(props.stateKey) };
                delete existingState.filterModel;
                pageRequest = _.assign(existingState, pageRequest);
                pageRequest.showAll = props.showAll;
                pageRequest.entityName = metadata?.entityName;
                EntityTableStateStorage.setState(props.stateKey, { ...pageRequest, filterModel: tableState.filterModel });
                const response = await props.onPageRequest(pageRequest);
                if (response?.records && response.total >= 0) {
                    setTableState((prevState) => ({ ...prevState, rows: response.records, total: response.total }));
                }
                if (response?.errorMsg) {
                    showAlert(response.errorMsg, "error");
                }
            } finally {
                clearTimeout(loadingTimeout);
                setTableState((prevState) => ({ ...prevState, isLoading: false }));
            }
        };

        onRecordsFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        tableState.pageIndex,
        tableState.pageSize,
        tableState.sortModel,
        metadata?.entityName,
        tableState.filterModel,
        props.forceFetch,
        props.filter,
        props.showAll,
        props.onPageRequest,
        tableState.columnVisibilityModel,
        fetchAllColumns,
        tableState.forceFetch,
        tableState.uiSettingsLoading,
    ]);

    useEffect(() => {
        return () => {
            if (!userPreferencesKey) {
                return;
            }
            const ui = uiSettings.current;
            if (!ui) {
                return;
            }
            setUserPreferences((prev) => {
                return {
                    ...prev,
                    [UserPreferenceTypes.ENTITY_TABLES]: {
                        ...prev[UserPreferenceTypes.ENTITY_TABLES],
                        [userPreferencesKey]: ui,
                    },
                };
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOnPageChange = useCallback((event: React.MouseEvent<HTMLButtonElement> | null, pageIndex: number) => {
        setTableState((prevState) => {
            const rowsWithoutNewRecords = prevState.rows.filter((row) => !row.isNew);
            return {
                ...prevState,
                rows: rowsWithoutNewRecords,
                pageIndex: pageIndex,
            };
        });
    }, []);
    const handleOnPageSizeChange = useCallback(
        (event?: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, pageSize?: number) => {
            const newPageSize = event?.target.value ? Number(event.target.value) : pageSize ? pageSize : null;
            if (!newPageSize) {
                return;
            }
            setTableState((prevState) => {
                const firstRecord = prevState.pageIndex * prevState.pageSize;
                return { ...prevState, pageSize: newPageSize, pageIndex: Math.floor(firstRecord / newPageSize) };
            });
            updateUiSettingsView(tableState.selectedPredefinedEntityTableViewId, "pageSize", newPageSize);
        },
        [tableState.selectedPredefinedEntityTableViewId, updateUiSettingsView]
    );

    const handleOnSortModelChange = (sortModel: GridSortModel = []) => {
        setTableState((prevState) => ({ ...prevState, sortModel: sortModel }));
    };
    const handleRowClick: GridEventListener<"rowClick"> = (params, event) => {
        const state = gridApiRef.current.state;
        if (_.isEmpty(state.editRows)) {
            event.preventDefault();
            event.stopPropagation();
            if (typeof props.onRecordSelected === "function") {
                props.onRecordSelected(params.row);
            }
        }
    };
    const handleCellClick: GridEventListener<"cellClick"> = (params, event) => {
        if (event.ctrlKey) {
            event.stopPropagation();
        }
    };
    const handleFilterModelChanged = useCallback(
        (filterModel: GridFilterModel) => {
            const columns = props.showQuickSearch ? props.columns : undefined;
            const filter = EntityHelper.prepareFilterObject(filterModel, columns, props.quickSearchColumns);
            setTableState((prevState) => {
                return {
                    ...prevState,
                    pageIndex: 0,
                    customFilter: filter,
                    filterModel: filterModel,
                };
            });
            const selectedView = tableState.predefinedEntityTableViews.find((view) => view.id === tableState.selectedPredefinedEntityTableViewId);
            if (!selectedView) {
                return;
            }
            uiSettings.current = {
                ...uiSettings.current,
                predefinedEntityTableViews: uiSettings.current?.predefinedEntityTableViews?.map((view) => {
                    if (view.id === selectedView.id) {
                        return { ...view, filterModel };
                    }
                    return view;
                }),
            };
        },
        [props.columns, props.showQuickSearch, tableState.predefinedEntityTableViews, tableState.selectedPredefinedEntityTableViewId]
    );

    const handleRowsModeChanged = (newModel: GridRowModesModel) => {
        setTableState((prevState) => ({ ...prevState, rowModesModel: newModel }));
    };

    const handleRowEditStart = (params: GridRowParams, event: MuiEvent<React.SyntheticEvent>) => {
        event.defaultMuiPrevented = true;
    };
    const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
        event.defaultMuiPrevented = true;
    };
    const handleRowEditCommit: GridEventListener<"rowEditCommit"> = (params, event) => {
        event.defaultMuiPrevented = true;
    };
    const handleRowFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        const row = event.currentTarget;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const id = row!.dataset.id!;
        setTableState((prev) => ({ ...prev, selectedRow: { id } }));
    }, []);

    const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
        setTableState((prev) => ({
            ...prev,
            columnVisibilityModel: model,
        }));
        updateUiSettingsView(tableState.selectedPredefinedEntityTableViewId, "columnVisibilityModel", model);
    };

    const basePaginationProps = {
        rowsPerPageOptions: props.hideRowsPerPage ? [] : !props.showAll ? props.pageSizeOptions || DefaultPageSizeOptions : [],
        onRowsPerPageChange: !props.showAll && handleOnPageSizeChange,
        onPageChange: !props.showAll && handleOnPageChange,
        component: "div",
        page: !props.showAll && tableState.rows.length ? tableState.pageIndex : 0,
        rowsPerPage: !props.showAll && tableState.pageSize,
        showFirstButton: true,
        showLastButton: true,
        getItemAriaLabel: (type: "first" | "last" | "next" | "previous") => type,
        count: tableState.total,
        sx: { borderBottom: "none" },
    } as TablePaginationBaseProps;
    const paginationProps = props.overridePaginationProps ? props.overridePaginationProps(basePaginationProps) : basePaginationProps;
    const customToolbar = useCallback(
        (toolbarProps: any) => {
            toolbarSetAnchor.current = toolbarProps.setAnchorEl;
            return (
                <CustomGridToolbar
                    {...toolbarProps}
                    showAll={props.showAll}
                    tableTitle={props.title}
                    totalRecords={tableState.total}
                    options={{ ...props.toolbarOptions }}
                    quickSearch={props.showQuickSearch}
                    showPagination={props.pagination}
                    inlineTitle={props.inlineTitle}
                    quickSearchAutofocus={props.quickSearchAutofocus}
                />
            );
        },
        [props.inlineTitle, props.pagination, props.showAll, props.showQuickSearch, props.title, props.toolbarOptions, tableState.total]
    );
    const customColumnMenu = useCallback((columnMenuProps: any) => <ColumnMenu {...columnMenuProps} setPanelAnchorEl={setPanelAnchorEl} />, []);
    const footer = useCallback(() => <Footer legendItems={legend} />, [legend]);
    const getRowClassName = useCallback(
        (params: GridRowClassNameParams) => {
            if (!legend) {
                return "";
            }
            const legendItem = legend.find((legendItem) => legendItem.applyClass(params));
            if (!legendItem) {
                return "";
            }
            const rowColor = legendItem.color;
            return `entityTable__row--${rowColor}`;
        },
        [legend]
    );
    const onResize = useCallback(
        ({ colDef }: GridColumnResizeParams) => {
            columnsResized.current = true;
            const getColumns = () => {
                const selectedView = uiSettings.current?.predefinedEntityTableViews?.find((view) => view.id === tableState.selectedPredefinedEntityTableViewId);
                if (!selectedView) {
                    return uiSettings.current?.columns || [];
                }
                if (!selectedView.columns) {
                    return [];
                }
                return selectedView.columns;
            };
            const columns = [...getColumns()];
            const columnIdx = columns.findIndex((c: { field: string }) => c.field === colDef.field);
            if (columnIdx !== -1) {
                columns[columnIdx] = { field: colDef.field, width: colDef.width };
            } else {
                columns.push({ field: colDef.field, width: colDef.width });
            }
            updateUiSettingsView(tableState.selectedPredefinedEntityTableViewId, "columns", columns);
        },
        [tableState.selectedPredefinedEntityTableViewId, updateUiSettingsView]
    );
    const onColumnOrderChange = useCallback(() => {
        const visibleColumns = gridApiRef.current?.getVisibleColumns();
        if (!visibleColumns?.length) {
            return;
        }
        const orderedFields = visibleColumns.map((c: { field: string }) => c.field);
        updateUiSettingsView(tableState.selectedPredefinedEntityTableViewId, "columnOrder", orderedFields);
    }, [gridApiRef, tableState.selectedPredefinedEntityTableViewId, updateUiSettingsView]);
    const onPinnedColumnsChange = useCallback(
        (pinnedColumns: GridPinnedColumns) => {
            setTableState((prev) => ({ ...prev, pinnedColumns }));
            updateUiSettingsView(tableState.selectedPredefinedEntityTableViewId, "pinnedColumns", pinnedColumns);
        },
        [tableState.selectedPredefinedEntityTableViewId, updateUiSettingsView]
    );
    const setDefaultPredefinedView = useCallback(
        (view: PredefinedEntityTableView) => {
            return {
                ...view,
                columns: EntityHelper.formatColumns(
                    cloneDeep(props.columns),
                    excludeBaseColumns,
                    setActionsColumn,
                    setPanelToggleColumn,
                    props.disableFilterOnBaseColumns,
                    metadata?.columns,
                    formattedSortModel,
                    applyFilterOnHeader
                ),
                customFilter: props.filter,
                pageSize: constantPageSize || props.pageSize,
                clientQuery: {
                    rules: [],
                },
                columnOrder: uiSettings.current?.columnOrder || props.columns.map((el) => el.field),
                columnVisibilityModel: props.columnVisibilityModel,
                pinnedColumns: props.pinnedColumns,
                filterModel: initialRequest?.filterModel || ({ items: [] } as GridFilterModel),
            };
        },
        [
            constantPageSize,
            applyFilterOnHeader,
            excludeBaseColumns,
            formattedSortModel,
            initialRequest?.filterModel,
            metadata?.columns,
            props.columnVisibilityModel,
            props.columns,
            props.disableFilterOnBaseColumns,
            props.filter,
            props.pageSize,
            props.pinnedColumns,
            setActionsColumn,
            setPanelToggleColumn,
        ]
    );
    const clearPreferences = useCallback(() => {
        const selectedViewId = tableState.selectedPredefinedEntityTableViewId;
        if (selectedViewId) {
            if (!uiSettings.current || !uiSettings.current.predefinedEntityTableViews) {
                return;
            }
            setTableState((prev) => {
                return {
                    ...prev,
                    predefinedEntityTableViews: prev.predefinedEntityTableViews.map((view) => {
                        if (view.id !== selectedViewId) {
                            return view;
                        }
                        return setDefaultPredefinedView(view);
                    }),
                };
            });
            let clearedView: PredefinedEntityTableView | null = null;
            uiSettings.current.predefinedEntityTableViews.forEach((view) => {
                if (view.id !== selectedViewId) {
                    return view;
                }
                clearedView = setDefaultPredefinedView(view);
            });
            uiSettings.current.selectedPredefinedEntityTableViewId = selectedViewId;
            userPreferencesService.clearPreferences({
                preference: UserPreferenceTypes.ENTITY_TABLES,
                key: userPreferencesKey,
                predefinedEntityTableViewId: selectedViewId,
                predefinedEntityTableView: clearedView,
                appName: currentAppName,
            });
            setTimeout(() => {
                const ui = uiSettings.current;
                if (!ui) {
                    return;
                }
                setUserPreferences((prev) => {
                    return {
                        ...prev,
                        [UserPreferenceTypes.ENTITY_TABLES]: {
                            ...prev[UserPreferenceTypes.ENTITY_TABLES],
                            [userPreferencesKey]: ui,
                        },
                    };
                });
            }, 200);
            return;
        } else if (!tableState.predefinedEntityTableViews.length) {
            uiSettings.current = null;
            setTableState((prev) => {
                return {
                    ...prev,
                    columnVisibilityModel,
                    pinnedColumns: props.pinnedColumns || ({} as GridPinnedColumns),
                    pageSize: constantPageSize || props.pageSize || 10,
                    predefinedEntityTableViews: [],
                };
            });
            userPreferencesService.clearPreferences({ preference: UserPreferenceTypes.ENTITY_TABLES, key: userPreferencesKey, appName: currentAppName });
            setTimeout(() => {
                const preferencesCopy = cloneDeep(userPreferences);
                if (userPreferencesKey in preferencesCopy.entityTables) {
                    delete preferencesCopy.entityTables[userPreferencesKey];
                }
                setUserPreferences(preferencesCopy);
            }, 200);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnVisibilityModel, props.pageSize, props.pinnedColumns, setUserPreferences, userPreferencesKey, uiSettings.current]);
    const manageColumnsOnClick = useCallback(() => {
        if (!gridApiRef.current) {
            return;
        }
        const columnHeaderEl = gridApiRef.current?.rootElementRef?.current?.querySelector(".MuiDataGrid-columnHeaders") as HTMLElement;
        toolbarSetAnchor.current && toolbarSetAnchor.current(columnHeaderEl as HTMLElement);
    }, [gridApiRef]);
    const getOptionButtonItems = useCallback(() => {
        const saveViewLabel = tableState.predefinedEntityTableViews.length > 1 ? "Save all views" : "Save view";
        const predefinedViewButtons = [
            <MenuItem
                key="menu-item-advanced-filter"
                onClick={openAdvancedQueryBuilder}
                disabled={!tableState.predefinedEntityTableViews.length || (!hasSettingsPermission && selectedViewIsAdminDefined) || selectedViewIsStatmapDefined}
            >
                <Typography>Redefine view</Typography>
            </MenuItem>,
            <MenuItem key="menu-item-organize-views-filter" onClick={openOrganizeViewsDialog} disabled={!tableState.predefinedEntityTableViews.length}>
                <Typography>Organize views</Typography>
            </MenuItem>,
            <MenuItem key="menu-item-copy-as-view" onClick={copyAsPredefinedEntityTableView}>
                <Typography>Copy as view</Typography>
            </MenuItem>,
        ];
        return [
            <MenuItem
                key="menu-item-reset-preferences"
                onClick={clearPreferences}
                disabled={(!hasSettingsPermission && selectedViewIsAdminDefined) || selectedViewIsStatmapDefined}
            >
                <Typography>Reset preferences</Typography>
            </MenuItem>,
            <MenuItem key="menu-item-manage-columns" onClick={manageColumnsOnClick}>
                <GridToolbarColumnsButton
                    startIcon={null}
                    disableRipple
                    sx={{
                        padding: 0,
                        color: "rgba(0, 0, 0, 0.87)",
                        justifyContent: "flex-start",
                        fontWeight: 400,
                        fontSize: "1rem",
                        fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                        "&:hover": {
                            backgroundColor: "transparent",
                        },
                    }}
                />
            </MenuItem>,
            experimentalQueryBuilder ? predefinedViewButtons : null,
            <MenuItem key="menu-item-save-view" onClick={saveUiPreferences} disabled={(!hasSettingsPermission && selectedViewIsAdminDefined) || selectedViewIsStatmapDefined}>
                <Typography>{saveViewLabel}</Typography>
            </MenuItem>,
            !props.showAll && !constantPageSize ? (
                <MenuItem key="menu-item-rows-per-page" onClick={toggleOpenPageSizeMenu}>
                    <Typography>{`Rows per page: ${tableState.pageSize}`}</Typography>
                    {isPageSizeMenuOpen ? <ExpandLess /> : <ExpandMore />}
                </MenuItem>
            ) : null,
            !constantPageSize ? (
                <Collapse key="menu-item-collapse-pages" in={isPageSizeMenuOpen}>
                    {(pageSizeOptions || DefaultPageSizeOptions).map((opt) => {
                        const size = typeof opt === "number" ? opt : opt.value;
                        return (
                            <MenuItem key={"menu-item-page-size-" + size} onClick={() => handleOnPageSizeChange(undefined, size)} sx={{ pl: 4 }}>
                                <Typography>Set page size {size}</Typography>
                            </MenuItem>
                        );
                    })}
                </Collapse>
            ) : null,
        ].filter((el) => el);
    }, [
        tableState.predefinedEntityTableViews.length,
        tableState.pageSize,
        openAdvancedQueryBuilder,
        hasSettingsPermission,
        selectedViewIsAdminDefined,
        selectedViewIsStatmapDefined,
        openOrganizeViewsDialog,
        copyAsPredefinedEntityTableView,
        clearPreferences,
        manageColumnsOnClick,
        experimentalQueryBuilder,
        saveUiPreferences,
        props.showAll,
        toggleOpenPageSizeMenu,
        isPageSizeMenuOpen,
        handleOnPageSizeChange,
        pageSizeOptions,
        constantPageSize,
    ]);
    const context: EntityTableContext = useMemo(
        () => ({
            setDateFilterPopper,
            dateFilterPopper,
            apiRef: gridApiRef,
            showAlert,
            openAdvancedQueryBuilder,
            closeAdvancedQueryBuilder,
            updateAdvancedQueryBuilderQuery,
            selectPredefinedEntityTable,
            updateAdvancedQueryBuilderName,
            updatePredefinedView,
            onViewSave,
            onViewCancel,
            modifyView,
            deleteTableView,
            experimentalQueryBuilder,
            tableState,
            handleOnPageChange,
            updatePredefinedEntityTableViewsOrder,
            closeViewOrganizeModal,
            onOrganizeViewsCancel,
            onOrganizeViewsSave,
            orderedPredefinedTableViews,
            metadata,
        }),
        [
            dateFilterPopper,
            gridApiRef,
            showAlert,
            openAdvancedQueryBuilder,
            closeAdvancedQueryBuilder,
            updateAdvancedQueryBuilderQuery,
            selectPredefinedEntityTable,
            updateAdvancedQueryBuilderName,
            updatePredefinedView,
            onViewSave,
            onViewCancel,
            modifyView,
            deleteTableView,
            experimentalQueryBuilder,
            tableState,
            handleOnPageChange,
            updatePredefinedEntityTableViewsOrder,
            closeViewOrganizeModal,
            onOrganizeViewsCancel,
            onOrganizeViewsSave,
            orderedPredefinedTableViews,
            metadata,
        ]
    );
    overrideColumns && overrideColumns(metadata?.entityName, columns);

    if (hideEmpty && !tableState.total && !tableState.filterModel.items.length) {
        return null;
    }

    const table = (
        <EntityTableProvider value={context}>
            <DateFilterModal />
            <AdvancedQueryBuilderModal />
            <OrganizePredefinedViewsModal />
            <DataGridPremium
                slots={{
                    columnMenu: customColumnMenu,
                    noRowsOverlay: CustomNoRowsOverlay,
                    toolbar: !props.hideToolbar ? customToolbar : null,
                    footer: footer,
                }}
                slotProps={{
                    noRowsOverlay: { noRecordsImage: props.noRecordsImage, noRecordsMessage: props.noRecordsMessage },
                    toolbar: {
                        rowMode,
                        setTableState,
                        onSave: props.onEditRecordSave,
                        paginationProps,
                        anchorEl: panelAnchorEl,
                        setAnchorEl: setPanelAnchorEl,
                        getOptionButtonItems,
                    },
                    row: { onFocus: handleRowFocus },
                    panel: {
                        anchorEl: panelAnchorEl ? panelAnchorEl : gridApiRef.current?.rootElementRef?.current?.querySelector(".MuiDataGrid-columnHeaders"),
                        placement: "bottom-start",
                    },
                    columnsPanel: {
                        getTogglableColumns: (col) => col.filter((c) => c.field !== DOCUMENTS_TABLE_ACTION_COLUMN_FIELD).map((c) => c.field),
                    },
                }}
                getRowClassName={getRowClassName}
                sx={{
                    maxHeight: props.gridMaxHeight,
                }}
                {...props}
                disableColumnMenu={props.disableColumnMenu || isPublicOnlyMode}
                disableColumnFilter={props.disableColumnFilter}
                className={!props.gridHeight ? props.className : undefined}
                apiRef={gridApiRef}
                autoHeight={props.gridHeight || props.gridMaxHeight ? false : true}
                rows={tableState.uiSettingsLoading ? [] : tableState.rows}
                editMode={props.editMode ? props.editMode : "row"}
                rowModesModel={tableState.rowModesModel}
                onRowModesModelChange={handleRowsModeChanged}
                onRowEditStart={handleRowEditStart}
                onRowEditStop={handleRowEditStop}
                onRowEditCommit={handleRowEditCommit}
                columns={columns}
                loading={tableState.isLoading}
                pagination={props.pagination === false || props.showAll ? false : true}
                paginationMode="server"
                rowCount={tableState.total}
                sortingMode="server"
                sortModel={tableState.sortModel}
                onSortModelChange={handleOnSortModelChange}
                filterMode="server"
                onFilterModelChange={handleFilterModelChanged}
                filterModel={tableState.filterModel}
                hideFooter={props.legend ? false : true}
                hideFooterRowCount={props.pagination === false ? true : false}
                hideFooterSelectedRowCount={props.pagination === false ? true : false}
                hideFooterPagination={true}
                onRowClick={props.onRowClick ? props.onRowClick : handleRowClick}
                onCellClick={props.onCellClick ? props.onCellClick : handleCellClick}
                keepNonExistentRowsSelected // clear selection with 'rowSelectionModel'
                columnVisibilityModel={tableState.columnVisibilityModel}
                onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                onColumnWidthChange={onResize}
                onColumnOrderChange={onColumnOrderChange}
                pinnedColumns={tableState.pinnedColumns}
                onPinnedColumnsChange={onPinnedColumnsChange}
            />
        </EntityTableProvider>
    );

    if (!props.gridHeight) {
        return table;
    }
    return (
        <div className={props.className} style={{ height: props.gridHeight }}>
            {table}
        </div>
    );
});

export type ICustomTogglePanelCol = CustomTogglePanelColDefinition | undefined;

export type CustomTogglePanelColDefinition = {
    headerName: string;
    renderCell: (params: GridRenderCellParams<any, any, any>) => ReactNode;
};

export default EntityTable;
